import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';

import {
  RadioOptionsWithConfirm,
  IQuestion,
  CardContainer,
  CardWithModal,
  TitleAndEditButton
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';

import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import OtherTravellers from './otherTravellers';
import { IOtherTravellers, Relationship } from '../../../../../types/index.d';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus, relationshipLabel } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { boolToYesNo } from '../../../../../utilities/formatting';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormAddHistoryPage1 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE4));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };
  const onEdit = () => dispatch(createNavigateToAction('/history/step-3'));

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  const { travellingWithOthers, otherTravellers = [] } = studentVisaPrepForm;

  // constants
  const currentStep = 1;
  const steps = 1;
  const sectionTitle = 'Other travellers';

  // component state
  const [complete, setComplete] = useState(false);
  const [otherTravellersState, setOtherTravellersState] = useState<IOtherTravellers[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setOtherTravellersState([
      ...otherTravellers,
      {
        nationality: '',
        familyName: '',
        givenName: '',
        dob: '',
        sameNationality: undefined,
        ukVisa: undefined,
        hasPassport: undefined,
        passportNumber: '',
        relationship: undefined,
        relationshipOther: '',
        adultOrChild: ''
      }
    ]);
    setShowModal([...otherTravellers.map(_ => false), false]);
  }, [otherTravellers]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      otherTravellers,
      travellingWithOthers
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, otherTravellers, travellingWithOthers]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteTraveller = (index: number): void => {
    onChange(VQ.OTHER_TRAVELLERS, [...otherTravellers.slice(0, index), ...otherTravellers.slice(index + 1)]);
  };

  const getCardItems = (details: IOtherTravellers) => {
    let cardItems = [
      {
        label: "What is this person's relationship to you?",
        value: relationshipLabel(details.relationship || '')
      }
    ];
    if (details.relationship === Relationship.OTHER) {
      cardItems = cardItems.concat([
        {
          label: 'Other relationship',
          value: details.relationshipOther
        }
      ]);
    }
    cardItems = cardItems.concat([
      {
        label: 'Are they an adult or child?',
        value: details.adultOrChild
      },
      {
        label: 'Nationality',
        value: details.nationality
      },
      {
        label: 'Always had the same nationality?',
        value: boolToYesNo(details.sameNationality)
      },
      {
        label: 'Family name',
        value: details.familyName
      },
      {
        label: 'Given name',
        value: details.givenName
      },
      {
        label: 'Date of birth',
        value: moment(details.dob).format('DD MMM YYYY')
      },
      {
        label: 'Visa for the UK?',
        value: boolToYesNo(details.ukVisa)
      },
      {
        label: 'Passport or Travel Document?',
        value: boolToYesNo(details.hasPassport)
      }
    ]);
    if (details.hasPassport === true) {
      cardItems = cardItems.concat([
        {
          label: 'Document Number',
          value: details.passportNumber
        }
      ]);
    }
    return cardItems;
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <TitleAndEditButton title="You indicated you are coming to the UK with others" onClick={onEdit} />

      <RadioOptionsWithConfirm
        key={VQ.TRAVELLING_WITH_OTHERS}
        id={VQ.TRAVELLING_WITH_OTHERS}
        label={`Will you be travelling to the UK with anyone you have not mentioned in the previous sections?`}
        onClick={(question: IQuestion) => onChange(VQ.TRAVELLING_WITH_OTHERS, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: travellingWithOthers === true
          },
          {
            char: 'N',
            label: 'No',
            selected: travellingWithOthers === false
          }
        ]}
      />

      {travellingWithOthers === true ? (
        <CardContainer title={`How many?`}>
          {otherTravellersState.map((details: IOtherTravellers, index: number) => (
            <CardWithModal
              key={index}
              id={`other-travellers-${index}`}
              index={index}
              modalTitle={'Add Other Traveller'}
              cardItems={getCardItems(details)}
              onRemove={deleteTraveller}
              addButtonLabel={`Add Other Traveller`}
              addAnotherButtonLabel={`I have another Traveller to add`}
              hasAddMoreButton={
                details.nationality === '' &&
                details.familyName === '' &&
                details.givenName === '' &&
                details.dob === '' &&
                details.sameNationality === undefined &&
                details.ukVisa === undefined &&
                details.hasPassport === undefined &&
                details.passportNumber === '' &&
                details.relationship === undefined &&
                details.relationshipOther === '' &&
                details.adultOrChild === ''
              }
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <OtherTravellers
                index={index}
                nationality={details.nationality}
                familyName={details.familyName}
                givenName={details.givenName}
                dob={details.dob}
                sameNationality={details.sameNationality}
                ukVisa={details.ukVisa}
                hasPassport={details.hasPassport}
                passportNumber={details.passportNumber}
                relationship={details.relationship}
                relationshipOther={details.relationshipOther}
                adultOrChild={details.adultOrChild}
                onSaveAndClose={(details: IOtherTravellers) => {
                  const travellers = [...otherTravellers];
                  travellers[index] = {
                    ...details
                  };
                  onChange(VQ.OTHER_TRAVELLERS, travellers);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage1;
